import type { DepthMap } from '../renderer/types.ts';
import { storage } from '../storage/index.ts';
import { DEPTH_PIPELINE, decodeDepthFile, encodeDepthFile } from './process.ts';
import type { DepthRequest, DepthResponse } from './protocol.ts';

/**
 * Depth maps per photo: cached by photo hash (OPFS / IndexedDB, key format of 05 §2:
 * '<sha256[0..32]>-<pipeline>'), computed lazily in a worker otherwise. Failures are not
 * memoized (a later open retries); the editor never waits for depth.
 */
export type DepthProgress = (stage: 'download' | 'load' | 'infer', progress?: number) => void;

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<
  number,
  { resolve: (m: DepthMap) => void; reject: (e: Error) => void; onProgress?: DepthProgress }
>();
const inflight = new Map<string, Promise<DepthMap>>();

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module', name: 'depth' });
  worker.onmessage = (e: MessageEvent<DepthResponse>) => {
    const msg = e.data;
    const p = pending.get(msg.id);
    if (!p) return;
    if (msg.type === 'progress') p.onProgress?.(msg.stage, msg.progress);
    else if (msg.type === 'result') {
      pending.delete(msg.id);
      p.resolve({ width: msg.width, height: msg.height, data: msg.data });
    } else {
      pending.delete(msg.id);
      p.reject(new Error(msg.message));
    }
  };
  worker.onerror = (e) => {
    for (const p of pending.values()) p.reject(new Error(e.message || 'The depth worker crashed.'));
    pending.clear();
    worker?.terminate();
    worker = null; // recreated on the next request
  };
  return worker;
}

export const depthCacheKey = (photoSha: string) => `cache/depth/${photoSha.slice(0, 32)}-${DEPTH_PIPELINE}`;

export async function cachedDepth(photoSha: string): Promise<DepthMap | null> {
  const blob = await storage().keys.get(depthCacheKey(photoSha));
  if (!blob) return null;
  return decodeDepthFile(new Uint8Array(await blob.arrayBuffer()));
}

/** The depth map for a photo (cache, else the worker). `photo` must decode to the stored JPEG. */
export function depthFor(photoSha: string, photo: Blob, onProgress?: DepthProgress): Promise<DepthMap> {
  const running = inflight.get(photoSha);
  if (running) return running;
  const job = (async () => {
    const cached = await cachedDepth(photoSha);
    if (cached) return cached;
    const bitmap = await createImageBitmap(photo, { colorSpaceConversion: 'none', imageOrientation: 'from-image' });
    const id = nextId++;
    const map = await new Promise<DepthMap>((resolve, reject) => {
      pending.set(id, { resolve, reject, onProgress });
      const req: DepthRequest = { type: 'estimate', id, bitmap, preferWebGpu: true };
      getWorker().postMessage(req, [bitmap]);
    });
    const file = encodeDepthFile(map.width, map.height, map.data);
    await storage()
      .keys.put(depthCacheKey(photoSha), new Blob([file as Uint8Array<ArrayBuffer>]))
      .catch(() => {});
    return map;
  })();
  inflight.set(photoSha, job);
  job.finally(() => inflight.delete(photoSha)).catch(() => {});
  return job;
}

/** Frees the model (e.g. when leaving the editor on memory-constrained devices). */
export function disposeDepthWorker(): void {
  worker?.terminate();
  worker = null;
  for (const p of pending.values()) p.reject(new Error('Depth estimation was stopped.'));
  pending.clear();
}
